import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import {
  Action,
  createSelector,
  Select,
  Selector,
  State,
  StateContext,
} from "@ngxs/store";
import { AuthUser } from "app/core/auth/core/auth-user.model";
import { AuthState } from "app/core/auth/core/auth.state";
import { Map } from "immutable";
import { ToastrService } from "ngx-toastr";
import { Observable, of } from "rxjs";
import {
  catchError,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
} from "rxjs/operators";
import { FormStatus } from "~app/company/shared/model/company.model";
import { LegacyMemberService } from "~app/company/shared/service/legacy-member.service";
import { ReceiptModel } from "~app/shopping-cart/receipt/state/receipt.model";
import { AnnualDeclarationFormCollection } from "../model/annual-declaration.model";
import {
  AttachedDocuments,
  CompanyAmalgamation,
  CompanyIdentification,
  CompanyOtherEstablishment,
  CompanyOtherEstablishmentFirebase,
  LegalPerson,
  ShareHolderMember,
} from "../model/company-identification.model";
import {
  MandatedIdentification,
  MandatedIdentificationType,
} from "../model/mandatedIdentification.model";
import { AnnualDeclarationCommandApiService } from "../service/annual-declaration-command-api.service";
import {
  AnnualDeclarationFormFirebaseService,
  VersionType,
} from "../service/annual-declaration-form-firebase.service";
import { AnnualDeclarationQueryService } from "../service/annual-declaration-query.service";
import {
  CancelDocumentLoad,
  CompleteAnnualDeclarationForm,
  CompleteAnnualDeclarationFormFailure,
  CompleteAnnualDeclarationFormSuccess,
  DeleteDocument,
  DeleteDocumentFailure,
  DeleteDocumentSuccess,
  DownloadSummary,
  DownloadSummaryFailure,
  DownloadSummarySuccess,
  GenerateAnnualDeclarationPdf,
  GenerateAnnualDeclarationPdfFailure,
  GenerateAnnualDeclarationPdfSuccess,
  InitAnnualDeclarationForm,
  InitAnnualDeclarationFormFailure,
  InitAnnualDeclarationFormSuccess,
  LoadAnnualDeclarationForm,
  LoadAnnualDeclarationFormFailure,
  LoadAnnualDeclarationFormMembers,
  LoadAnnualDeclarationFormSuccess,
  LoadCompanyOtherEstablishmentFirebase,
  LoadDeclarationYear,
  LoadDeclarationYearSuccess,
  LoadFailStrategy,
  SaveAnnualDeclarationForm,
  SaveAnnualDeclarationFormFailure,
  SaveAnnualDeclarationFormSuccess,
  SelectLegalEntity,
  SelectLegalEntitySuccess,
  SetProcessingCompanySubscription,
  UploadDocument,
  UploadDocumentFailure,
  UploadDocumentSuccess,
} from "./annual-declaration.actions";

export class AnnualDeclarationStateModel {
  saving: boolean;
  formCollections: Map<string, AnnualDeclarationFormCollection> = Map();
  initializing = false;
  fileLoading = false;
  isDeclarationFormLoading = true;
  selectedLegalEntityId: string;
  formStates: FormStatus[];
  year: number;
  processingCommpanySubscription: boolean;
  paymentConfirmation: ReceiptModel;
  otherEstablishmentFireBase: CompanyOtherEstablishmentFirebase[];
}

@State<AnnualDeclarationStateModel>({
  name: "annualDeclaration",
  defaults: new AnnualDeclarationStateModel(),
})
@Injectable()
export class AnnualDeclarationState {
  constructor(
    private annualDeclarationCommandService: AnnualDeclarationCommandApiService,
    private annualDeclarationFormFirebaseService: AnnualDeclarationFormFirebaseService,
    private annualDeclarationQueryService: AnnualDeclarationQueryService,
    private legacyMemberService: LegacyMemberService,
    private toastrService: ToastrService,
    private translate: TranslateService
  ) { }

  @Select(AuthState.user) user$: Observable<AuthUser>;

  @Select(AnnualDeclarationState.formCollection)
  formCollection$: Observable<AnnualDeclarationFormCollection>;

  @Selector()
  static saving(state: AnnualDeclarationStateModel): boolean {
    return state.saving;
  }

  @Selector()
  static isDeclarationFormLoading(state: AnnualDeclarationStateModel): boolean {
    return state.isDeclarationFormLoading;
  }

  @Selector()
  static fileLoading(state: AnnualDeclarationStateModel): boolean {
    return state.fileLoading;
  }

  @Selector()
  static selectedLegalEntityId(state: AnnualDeclarationStateModel): string {
    return state.selectedLegalEntityId;
  }

  @Selector()
  static formCollection(
    state: AnnualDeclarationStateModel
  ): AnnualDeclarationFormCollection {
    return state.formCollections.get(state.selectedLegalEntityId);
  }

  @Selector()
  static formCollections(
    state: AnnualDeclarationStateModel
  ): Map<string, AnnualDeclarationFormCollection> {
    return state.formCollections;
  }

  @Selector()
  static legalEntityNumber(state: AnnualDeclarationStateModel): string {
    return state.formCollections.get(state.selectedLegalEntityId)
      ?.legalEntityNumber;
  }

  @Selector()
  static formId(state: AnnualDeclarationStateModel): string {
    return state.formCollections.get(state.selectedLegalEntityId)?.formId;
  }

  @Selector()
  static companyIdentification(
    state: AnnualDeclarationStateModel
  ): CompanyIdentification {
    return state.formCollections.get(state.selectedLegalEntityId)?.current
      ?.identification;
  }

  @Selector()
  static lastSaved(state: AnnualDeclarationStateModel): string {
    return state.formCollections.get(state.selectedLegalEntityId)
      ?.lastSavedDate;
  }

  @Selector()
  static OtherEstablishmentFirebase(state: AnnualDeclarationStateModel): CompanyOtherEstablishmentFirebase[] {
    return state.otherEstablishmentFireBase;
  }

  static companyOtherNames(version: VersionType) {
    return createSelector(
      [AnnualDeclarationState],
      (state: AnnualDeclarationStateModel) => {
        return version === VersionType.Current
          ? state.formCollections.get(state.selectedLegalEntityId)?.current
            .otherNames?.otherNames
          : state.formCollections.get(state.selectedLegalEntityId)?.original
            ?.otherNames?.otherNames;
      }
    );
  }

  static companyRepresentativeSubstitute(version: VersionType) {
    return createSelector(
      [AnnualDeclarationState],
      (state: AnnualDeclarationStateModel) => {
        return version === VersionType.Current
          ? state.formCollections.get(state.selectedLegalEntityId)?.current
            ?.representativeSubstitute
          : state.formCollections.get(state.selectedLegalEntityId)?.original
            ?.representativeSubstitute;
      }
    );
  }

  @Selector()
  static companyDirectors(state: AnnualDeclarationStateModel) {
    return state.formCollections.get(state.selectedLegalEntityId)?.current
      ?.directors?.directorsNumber;
  }

  @Selector()
  static companyOtherEstablishments(
    state: AnnualDeclarationStateModel
  ): CompanyOtherEstablishment[] {
    return state.formCollections.get(state.selectedLegalEntityId)?.current
      ?.otherEstablishments?.otherEstablishments;
  }

  @Selector()
  static companyOtherEstablishmentsOriginal(
    state: AnnualDeclarationStateModel
  ): CompanyOtherEstablishment[] {
    return state.formCollections.get(state.selectedLegalEntityId)?.original
      ?.otherEstablishments?.otherEstablishments;
  }

  @Selector()
  static companyVotingMembers(
    state: AnnualDeclarationStateModel
  ): ShareHolderMember[] {
    return state.formCollections.get(state.selectedLegalEntityId)?.current
      ?.votingMembers?.votingMembers;
  }

  @Selector()
  static companyVotingLegalPersons(
    state: AnnualDeclarationStateModel
  ): LegalPerson[] {
    return state.formCollections.get(state.selectedLegalEntityId)?.current
      ?.votingLegalPersons?.legalPersons;
  }

  static companyNonVotingNaturalPersons(version: VersionType) {
    return createSelector(
      [AnnualDeclarationState],
      (state: AnnualDeclarationStateModel) => {
        return version === VersionType.Current
          ? state.formCollections.get(state.selectedLegalEntityId)?.current
            ?.nonVotingNaturalPersons
          : state.formCollections.get(state.selectedLegalEntityId)?.original
            ?.nonVotingNaturalPersons;
      }
    );
  }

  static companyNonVotingLegalPersons(version: VersionType) {
    return createSelector(
      [AnnualDeclarationState],
      (state: AnnualDeclarationStateModel) => {
        return version === VersionType.Current
          ? state.formCollections.get(state.selectedLegalEntityId)?.current
            ?.nonVotingLegalPersons?.legalPersons
          : state.formCollections.get(state.selectedLegalEntityId)?.original
            ?.nonVotingLegalPersons?.legalPersons;
      }
    );
  }

  @Selector()
  static companyAmalgamation(
    state: AnnualDeclarationStateModel
  ): CompanyAmalgamation {
    return state.formCollections.get(state.selectedLegalEntityId)?.current
      ?.amalgamation;
  }

  @Selector()
  static attachedDocumentList(
    state: AnnualDeclarationStateModel
  ): AttachedDocuments[] {
    return state.formCollections.get(state.selectedLegalEntityId)
      ?.attachedDocuments;
  }

  @Selector()
  static summaryAttachment(
    state: AnnualDeclarationStateModel
  ): AttachedDocuments {
    return state.formCollections.get(state.selectedLegalEntityId)
      ?.summaryAttachment;
  }

  @Selector()
  static year(state: AnnualDeclarationStateModel): number {
    return state.year;
  }


  @Selector()
  static processingCommpanySubscription(state: AnnualDeclarationStateModel): boolean {
    return state.processingCommpanySubscription;
  }

  @Action(SaveAnnualDeclarationForm)
  saveAnnualDeclarationForm(
    {
      patchState,
      dispatch,
      getState,
    }: StateContext<AnnualDeclarationStateModel>,
    { annualDeclaration }: SaveAnnualDeclarationForm
  ) {
    if (getState().saving) {
      dispatch(new SaveAnnualDeclarationFormFailure("Form is already saving"));
    } else {
      patchState({ saving: true });

      annualDeclaration = JSON.parse(JSON.stringify(annualDeclaration));

      this.annualDeclarationFormFirebaseService
        .updateCurrentAnnualDeclaration(
          getState().selectedLegalEntityId,
          annualDeclaration,
          getState().year
        )
        .then((_) => dispatch(new SaveAnnualDeclarationFormSuccess()))
        .catch((error) =>
          dispatch(new SaveAnnualDeclarationFormFailure(error))
        );
    }
  }

  @Action(SaveAnnualDeclarationFormSuccess)
  saveAnnualDeclarationFormSuccess(
    {
      patchState,
      dispatch,
      getState,
    }: StateContext<AnnualDeclarationStateModel>,
    action: SaveAnnualDeclarationFormSuccess
  ) {
    patchState({
      saving: false,
    });
    dispatch(
      new LoadAnnualDeclarationForm(
        getState().selectedLegalEntityId,
        LoadFailStrategy.DoNothing
      )
    );
  }

  @Action(LoadAnnualDeclarationForm)
  loadAnnualDeclarationForm(
    { dispatch, getState }: StateContext<AnnualDeclarationStateModel>,
    { legalEntityId, loadFailStrategy }: LoadAnnualDeclarationForm
  ) {
    return this.annualDeclarationFormFirebaseService
      .getAnnualDeclarationFormCollection(legalEntityId, getState().year)
      .pipe(
        take(1),
        tap((formCollection) => {
          if (!!formCollection?.state) {
            dispatch(
              new LoadAnnualDeclarationFormMembers(
                legalEntityId,
                formCollection
              )
            );
          } else if (loadFailStrategy === LoadFailStrategy.Init) {
            return dispatch(
              new LoadAnnualDeclarationFormFailure(legalEntityId)
            );
          }
        })
      );
  }

  @Action(LoadAnnualDeclarationFormMembers)
  loadMembers(
    { dispatch, patchState }: StateContext<AnnualDeclarationStateModel>,
    { legalEntityId, formCollection }: LoadAnnualDeclarationFormMembers
  ) {
    const establishments =
      formCollection?.current?.otherEstablishments?.otherEstablishments;
    var permitNumbers: string[] = [];
    establishments?.forEach((e) =>
      e.members?.forEach((m) => {
        if (!permitNumbers.includes(m.permitNumber)) {
          permitNumbers.push(m.permitNumber);
        }
      })
    );

    this.legacyMemberService
      .getListByPermitNumber(permitNumbers)
      .pipe(
        tap((_) => {
          return patchState({
            saving: true,
            fileLoading: true,
            isDeclarationFormLoading: true,
          });
        }),
        tap((members) => {
          this.legacyMemberService.setMembersCache(members, permitNumbers);
          dispatch(
            new LoadAnnualDeclarationFormSuccess(legalEntityId, formCollection)
          );
        })
      )
      .subscribe();
  }

  @Action(LoadAnnualDeclarationFormSuccess)
  loadAnnualDeclarationFormSuccess(
    { patchState, getState }: StateContext<AnnualDeclarationStateModel>,
    { legalEntityId, formCollection }: LoadAnnualDeclarationFormSuccess
  ) {
    return this.user$.pipe(
      take(1),
      map(
        (user) =>
          new MandatedIdentification(
            user.roles.includes("legalEntityPrincipalRepresentative")
              ? MandatedIdentificationType.RespondentMember
              : MandatedIdentificationType.MandatedRepresentative,
            user.name,
            user.email
          )
      ),
      map((mandatedIdentification) => {
        if (formCollection != undefined) {
          formCollection.mandatedIdentification = mandatedIdentification;
          let formCollections = getState().formCollections;
          return formCollections.set(legalEntityId, formCollection);
        } else {
          return getState().formCollections;
        }
      }),
      tap((formCollections) => {
        return patchState({
          saving: false,
          fileLoading: false,
          isDeclarationFormLoading: false,
          formCollections: formCollections,
        });
      })
    );
  }

  @Action(LoadAnnualDeclarationFormFailure)
  loadAnnualDeclarationFormFailure(
    {
      dispatch,
      patchState,
      getState,
    }: StateContext<AnnualDeclarationStateModel>,
    { legalEntityId }: LoadAnnualDeclarationFormFailure
  ) {
    let formCollections = getState().formCollections;
    formCollections.delete(legalEntityId);

    patchState({
      isDeclarationFormLoading: false,
      fileLoading: false,
      formCollections,
    });
    if (!!legalEntityId) {
      dispatch(new InitAnnualDeclarationForm(legalEntityId));
    }
  }

  @Action(InitAnnualDeclarationForm)
  initAnnualDeclarationForm(
    {
      dispatch,
      patchState,
      getState,
    }: StateContext<AnnualDeclarationStateModel>,
    { legalEntityId }: InitAnnualDeclarationForm
  ) {
    patchState({
      saving: true,
    });

    if (!getState().initializing) {
      return this.annualDeclarationCommandService
        .initAnnualDeclarationForm(legalEntityId, getState().year)
        .pipe(
          tap((_) => patchState({ initializing: true })),
          tap((_) =>
            dispatch(new InitAnnualDeclarationFormSuccess(legalEntityId))
          ),
          catchError((_) =>
            dispatch(new InitAnnualDeclarationFormFailure(legalEntityId))
          )
        );
    }
  }

  @Action(InitAnnualDeclarationFormSuccess)
  initAnnualDeclarationFormSuccess(
    { dispatch, patchState }: StateContext<AnnualDeclarationStateModel>,
    { legalEntityId }: InitAnnualDeclarationFormSuccess
  ) {
    patchState({
      initializing: false,
    });
    dispatch(
      new LoadAnnualDeclarationForm(legalEntityId, LoadFailStrategy.DoNothing)
    );
  }

  @Action(InitAnnualDeclarationFormFailure)
  initAnnualDeclarationFormFailure(
    { patchState }: StateContext<AnnualDeclarationStateModel>,
    { }: InitAnnualDeclarationFormFailure
  ) {
    patchState({
      initializing: false,
    });
  }

  @Action(CompleteAnnualDeclarationForm)
  completeAnnualDeclaration(
    { dispatch, getState, patchState }: StateContext<AnnualDeclarationStateModel>,
    { paymentConfirmation }: CompleteAnnualDeclarationForm
  ) {
    var legalEntityId = getState().selectedLegalEntityId;

    // if there is payment, it is saved, otherwise, it is retrieved from a previous save
    if (!!paymentConfirmation) {
      this.annualDeclarationFormFirebaseService.updatePaymentConfirmation(
        legalEntityId,
        paymentConfirmation,
        getState().year
      );
      // optimistic behaviour, only for dentists
      this.annualDeclarationFormFirebaseService.updateAnnualDeclarationState(
        legalEntityId,
        FormStatus.Payed,
        getState().year
      );
    } else {
      paymentConfirmation =
        getState().formCollections?.get(legalEntityId)?.paymentConfirmation ??
        new ReceiptModel();
    }

    patchState({ paymentConfirmation: paymentConfirmation });

    var collection = getState().formCollections?.get(legalEntityId);

    return this.annualDeclarationCommandService
      .completeAnnualDeclarationForm(
        legalEntityId,
        paymentConfirmation,
        collection?.current,
        collection?.mandatedIdentification,
        getState().year
      )
      .pipe(
        tap((_) =>
          dispatch(new CompleteAnnualDeclarationFormSuccess(legalEntityId))
        ),
        catchError((error) =>
          dispatch(new CompleteAnnualDeclarationFormFailure(error))
        )
      );
  }

  @Action(GenerateAnnualDeclarationPdf)
  generateAnnualDeclarationPdf(
    {
      dispatch,
      patchState,
      getState,
    }: StateContext<AnnualDeclarationStateModel>,
    { legalEntityId }: GenerateAnnualDeclarationPdf
  ) {
    if (!legalEntityId) {
      legalEntityId = getState().selectedLegalEntityId;
    }

    let formCollections = getState().formCollections;
    let paymentConfirmation = getState().paymentConfirmation;

    let form = {
      ...formCollections.get(legalEntityId),
      isSummaryDownloading: true,
    };

    if (paymentConfirmation !== undefined) {
      form = {
        ...form,
        paymentConfirmation: paymentConfirmation
      }
    }

    patchState({
      formCollections: formCollections.set(legalEntityId, form),
    });

    return this.annualDeclarationQueryService
      .generatePdf(legalEntityId, getState().formCollections.get(legalEntityId))
      .pipe(
        tap((pdf) =>
          dispatch(new GenerateAnnualDeclarationPdfSuccess(legalEntityId, pdf))
        ),
        catchError((error) =>
          dispatch(
            new GenerateAnnualDeclarationPdfFailure(error, legalEntityId)
          )
        )
      );
  }

  @Action(GenerateAnnualDeclarationPdfSuccess)
  generateAnnualDeclarationPdfSuccess(
    { patchState, getState }: StateContext<AnnualDeclarationStateModel>,
    { legalEntityId, pdf }: GenerateAnnualDeclarationPdfSuccess
  ) {
    let formCollections = getState().formCollections;
    let form = {
      ...formCollections.get(legalEntityId),
      isSummaryDownloading: false,
    };

    patchState({
      formCollections: formCollections.set(legalEntityId, form),
    });

    return this.annualDeclarationCommandService
      .authorizeDocumentDownload(legalEntityId, pdf.documentId, pdf.fileName)
      .pipe(
        take(1),
        mergeMap((_) =>
          this.annualDeclarationFormFirebaseService.updateSummaryAttachment(
            legalEntityId,
            pdf,
            getState().year
          )
        )
      );
  }

  @Action(GenerateAnnualDeclarationPdfFailure)
  generateAnnualDeclarationPdfFailure(
    { patchState, getState }: StateContext<AnnualDeclarationStateModel>,
    { legalEntityId }: GenerateAnnualDeclarationPdfFailure
  ) {
    let formCollections = getState().formCollections;
    let form = {
      ...formCollections.get(legalEntityId),
      isSummaryDownloading: false,
    };

    patchState({
      formCollections: formCollections.set(legalEntityId, form),
    });

    return this.showError(
      "Pdf",
      this.translate.instant("pdf.unableToDownload")
    );
  }

  @Action(UploadDocument)
  uploadDocument(
    {
      dispatch,
      patchState,
      getState,
    }: StateContext<AnnualDeclarationStateModel>,
    { legalEntityId, type, file }: UploadDocument
  ) {
    patchState({
      fileLoading: true,
      saving: true
    });

    return this.annualDeclarationCommandService
      .uploadDocument(legalEntityId, type, file, getState().year)
      .pipe(
        tap((_) => dispatch(new UploadDocumentSuccess(legalEntityId))),
        catchError((error) => dispatch(new UploadDocumentFailure(error)))
      );
  }

  @Action(UploadDocumentFailure)
  onUploadDocumentFailure({ patchState }: StateContext<AnnualDeclarationStateModel>,
    { error }: UploadDocumentFailure
  ) {
    console.log(error);
    patchState({
      fileLoading: false,
      saving: false
    });
    return this.showError("Téléverser document", this.translate.instant("attachment.unableToUpload"));
  }

  @Action(DeleteDocument)
  deleteDocument(
    {
      dispatch,
      patchState,
      getState,
    }: StateContext<AnnualDeclarationStateModel>,
    { legalEntityId, documentId }: DeleteDocument
  ) {
    patchState({
      fileLoading: true,
      saving: true
    });

    return this.annualDeclarationCommandService
      .deleteDocument(legalEntityId, documentId, getState().year)
      .pipe(
        tap((_) => dispatch(new DeleteDocumentSuccess(legalEntityId))),
        catchError((error) => dispatch(new DeleteDocumentFailure(error)))
      );
  }


  @Action(DeleteDocumentFailure)
  onDeleteDocumentFailure({ patchState }: StateContext<AnnualDeclarationStateModel>,
    { error }: DeleteDocumentFailure
  ) {
    console.log(error);
    patchState({
      fileLoading: false,
      saving: false
    });
    return this.showError("Effacer document", this.translate.instant("attachment.unableToDelete"));
  }

  @Action(SelectLegalEntity)
  selectLegalEntity(
    { dispatch, patchState }: StateContext<AnnualDeclarationStateModel>,
    { legalEntityId }: SelectLegalEntity
  ) {
    patchState({
      selectedLegalEntityId: legalEntityId,
    });

    dispatch(new SelectLegalEntitySuccess(legalEntityId));
  }

  @Action(SelectLegalEntitySuccess)
  selectLegalEntitySuccess(
    { dispatch }: StateContext<AnnualDeclarationStateModel>,
    { legalEntityId }: SelectLegalEntitySuccess
  ) {
    dispatch(
      new LoadAnnualDeclarationForm(legalEntityId, LoadFailStrategy.DoNothing)
    );
  }

  private showError(title: string, message: string): Observable<void> {
    this.toastrService.error(message, title, {
      closeButton: true,
      timeOut: 4000,
      onActivateTick: true,
    });
    return of();
  }

  @Action(CancelDocumentLoad)
  cancelDocumentUpload(
    { patchState }: StateContext<AnnualDeclarationStateModel>,
    { }: CancelDocumentLoad
  ) {
    patchState({
      fileLoading: false,
    });
  }

  @Action(DownloadSummary)
  downloadSummary(
    { dispatch, getState, patchState }: StateContext<AnnualDeclarationStateModel>,
    { legalEntityId }: DownloadSummary
  ) {
    if (!legalEntityId) {
      legalEntityId = getState().selectedLegalEntityId;
    }

    let formCollections = getState().formCollections;
    let form = {
      ...formCollections.get(legalEntityId),
      isSummaryDownloading: true,
    };

    patchState({
      formCollections: formCollections.set(legalEntityId, form),
    });

    return this.annualDeclarationFormFirebaseService
      .getAnnualDeclarationFormCollection(legalEntityId, getState().year)
      .pipe(
        take(1),
        switchMap((col) =>
          this.annualDeclarationQueryService.generateSummaryPdf(legalEntityId, col)
        ),
        tap(
          (pdf) => dispatch(new DownloadSummarySuccess(legalEntityId, pdf)),
          catchError((error) => dispatch(new DownloadSummaryFailure(legalEntityId, error)))
        )
      );
  }

  @Action(DownloadSummarySuccess)
  onDownloadSummarySuccess(
    { patchState, getState }: StateContext<AnnualDeclarationStateModel>,
    { legalEntityId, pdf }: DownloadSummarySuccess
  ) {
    let formCollections = getState().formCollections;
    let form = {
      ...formCollections.get(legalEntityId),
      isSummaryDownloading: false,
    };

    patchState({
      formCollections: formCollections.set(legalEntityId, form),
    });

    return this.annualDeclarationCommandService
      .authorizeDocumentDownload(legalEntityId, pdf.documentId, pdf.fileName)
      .pipe(
        take(1),
        mergeMap((_) =>
          this.annualDeclarationFormFirebaseService.updateSummaryAttachment(
            legalEntityId,
            pdf,
            getState().year
          )
        )
      );
  }

  @Action(DownloadSummaryFailure)
  onDownloadSummaryFailure(
    { patchState, getState }: StateContext<AnnualDeclarationStateModel>,
    { legalEntityId }: DownloadSummaryFailure
  ) {
    let formCollections = getState().formCollections;
    let form = {
      ...formCollections.get(legalEntityId),
      isSummaryDownloading: false,
    };

    patchState({
      formCollections: formCollections.set(legalEntityId, form),
    });

    return this.showError(
      "Pdf",
      this.translate.instant("pdf.unableToDownload")
    );
  }


  @Action(LoadDeclarationYear)
  loadDeclarationYear(
    { dispatch }: StateContext<AnnualDeclarationStateModel>,
    { }: LoadDeclarationYear
  ) {
    return this.annualDeclarationFormFirebaseService.getDeclarationYear().pipe(
      tap((year) => dispatch(new LoadDeclarationYearSuccess(year))),
      catchError((error) =>
        dispatch(new LoadAnnualDeclarationFormFailure(error))
      )
    );
  }

  @Action(LoadDeclarationYearSuccess)
  loadDeclarationYearSuccess(
    { patchState }: StateContext<AnnualDeclarationStateModel>,
    { year }: LoadDeclarationYearSuccess
  ) {
    patchState({
      year,
    });
  }

  @Action(SetProcessingCompanySubscription)
  setProcessingCompanySubscription(
    { patchState }: StateContext<AnnualDeclarationStateModel>,
    { processing }: SetProcessingCompanySubscription
  ) {
    patchState({
      processingCommpanySubscription: processing,
    });
  }

  @Action(LoadCompanyOtherEstablishmentFirebase)
  loadCompanyOtherEstablishmentFirebase(
    { patchState }: StateContext<AnnualDeclarationStateModel>,
    { legalEntityId }: LoadCompanyOtherEstablishmentFirebase
  ) {
    return this.annualDeclarationFormFirebaseService.getEstablishmentMemberStatus(legalEntityId).pipe(
      map(result => {
        patchState({ otherEstablishmentFireBase: result })
      })
    );
  }
}
